import { useCallback,useState } from "react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import { Loader2 } from "lucide-react";

import { ConsultationPreview } from "components/ConsultationPreview/ConsultationPreview";
import { SearchInput } from "components/ui";
import { DataTablePagination } from "components/ui/DataTable/components/DataTablePagination/DataTablePagination";
import { useConsultationsPaginatedList } from "features/consultations/hooks/useConsultationsPaginatedList";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePracticeServices } from "features/practice/hooks/usePracticeServices";
import { createDateFromArray } from "utils/createDateFromArray";
import { pathGenerators } from "utils/routes";

import styles from "./ConsultationsTable.module.css";

export const ConsultationsTable = ({ patientIdVal, itemsPerPage = 10, currentConsultationId }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const currentPatientId = useCurrentPatientId();
  const patientId =
    patientIdVal !== undefined
      ? patientIdVal
      : currentPatientId;
  const apiRows = rowsPerPage === "All" ? 10000 : rowsPerPage;
  const { data, isLoading } = useConsultationsPaginatedList({
    patientId,
    page,
    search: debouncedSearch,
    rows: apiRows,
  });
  const { data: services } = usePracticeServices();

  const totalPages = rowsPerPage === "All" ? 1 : Math.ceil(data?.totalRecords / rowsPerPage);
  const start = rowsPerPage === "All" ? 1 : (page - 1) * rowsPerPage + 1;
  const end = rowsPerPage === "All" ? data?.totalRecords : Math.min(page * rowsPerPage, data?.totalRecords);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce((query) => {
      setDebouncedSearch(query);
    }, 300),
    []
  );

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <div className={styles.emptyState}>
          <Loader2 className={styles.loadingSpinner} size={24} />
          <p className={styles.emptyStateTitle}>Loading data...</p>
          <p className={styles.emptyStateText}>
            Please wait while we fetch the consultations.
          </p>
        </div>
      );
    }

    if (!data?.data?.length) {
      return (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>No consultations found</p>
          <p className={styles.emptyStateText}>
            {search
              ? "Try adjusting your search to find what you're looking for."
              : "There are no consultations to display at this time."}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.consultationPreviewWrapper}>
          {(data?.data || [])?.map((consultation) => (
            <ConsultationPreview
              onEditConsultation={
                String(consultation.id) === String(currentConsultationId)
                  ? undefined
                  : () =>
                      navigate(
                        pathGenerators.patient.consultationEdit({
                          patientId: consultation.patient?.id ?? patientId,
                          consultationId: consultation.id,
                        })
                      )
              }
              isActive={String(consultation.id) === String(currentConsultationId)}
              consultation={{
                id: consultation.id,
                title:
                  services?.find(
                    ({ id }) => id === consultation.mainPracticeServiceId
                  )?.name || "Consultation",
                date: createDateFromArray(consultation.created),
                consultationStaff: consultation.consultationStaff,
                patient: consultation.patient,
                attachments: {
                  prescriptions: consultation?.patientPrescriptions || [],
                  refferrals: [],
                },
              }}
              notes={consultation.consultationNoteList || []}
              key={consultation.id}
            />
          ))}
        </div>
        <DataTablePagination
          start={start}
          end={end}
          total={data?.totalRecords}
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
          rowsOptions={[10, 20, 50, 100, "All"]}
          rowsPerPage={rowsPerPage}
          onRowsChange={(val) => {
            setRowsPerPage(val);
            setPage(1);
          }}
        />
      </>
    );
  };

  return (
    <div className={styles.tableWarpper}>
      <div className={styles.table}>
        <div className={styles.tableTopBarWrapper}>
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              debouncedSearchHandler(value);
            }}
            placeholder="Search consultations"
          />
        </div>
        {renderTableContent()}
      </div>
    </div>
  );
};
